import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ReportsArea } from "./areas/reports/ReportsArea";
import { InsightsFilters } from "./areas/insights/InsightsFilters";
import { RangeSessions } from "./areas/range/RangeSessions";
import { RangeReportDetail } from "./areas/range/RangeReportDetail";
import { SearchResultsPage } from "./areas/search/SearchResultsPage";
import { CostOfPrecision } from "./areas/insights/CostOfPrecision";
import { TrendSummaries } from "./areas/insights/TrendSummaries";
import { ReloadingTable } from "./areas/reloading/ReloadingTable";
import { SuppressorsTable } from "./areas/suppressors/SuppressorsTable";
import { AmmoTable } from "./areas/ammo/AmmoTable";
import { AmmoAddButton } from "./areas/ammo/AmmoAddButton";
import { BallisticsAddButton } from "./areas/ballistics/BallisticsAddButton";
import { BallisticsFirearmHelp, BallisticsPageStatus, BallisticsToolbar, BenchListHead } from "./areas/ballistics/BallisticsToolbar";
import { BENCH_ADD_BUTTONS } from "./areas/ballistics/toolbar";
import { AmmoInventorySummary } from "./areas/ammo/AmmoInventorySummary";
import { EntryWorkspace } from "./areas/entry/EntryWorkspace";
import { SettingsTabs } from "./areas/settings/SettingsTabs";
import { AmmoOverview } from "./areas/ammo/AmmoOverview";
import { AmmoCaliberTotals } from "./areas/ammo/AmmoCaliberTotals";
import { FirearmsTable } from "./areas/firearms/FirearmsTable";
import { MaintenanceQueue } from "./areas/firearms/MaintenanceQueue";
import { FirearmManufacturers } from "./areas/firearms/FirearmManufacturers";
import { ActionBoard } from "./areas/home/ActionBoard";
import { HomeStatCards } from "./areas/home/HomeStatCards";
import { HomeResourceStatus } from "./areas/home/HomeResourceStatus";
import { LowStockWatchList } from "./areas/home/LowStockWatchList";
import { RecentActivity } from "./areas/home/RecentActivity";
import { BallisticsTable } from "./areas/ballistics/BallisticsTable";
import { BallisticsSummaryCards } from "./areas/ballistics/BallisticsSummaryCards";
import { CorrelationSnapshot } from "./areas/ballistics/CorrelationSnapshot";
import { ProfilePerformance } from "./areas/ballistics/ProfilePerformance";
import { LoadDevelopment } from "./areas/ballistics/LoadDevelopment";
import { ComponentStock } from "./areas/ballistics/ComponentStock";
import { Recipes } from "./areas/ballistics/Recipes";
import { BrassLots } from "./areas/ballistics/BrassLots";
import { Workups } from "./areas/ballistics/Workups";
import { BarrelWear } from "./areas/ballistics/BarrelWear";
import { InventoryPagination } from "./platform/InventoryPagination";
import { InventoryToolbar } from "./platform/InventoryToolbar";
import { AdminOrganizationsPanel } from "./areas/admin/AdminOrganizationsPanel";
import { AdminUsersPanel } from "./areas/admin/AdminUsersPanel";
import { AdminMembersPanel } from "./areas/admin/AdminMembersPanel";
import { AdminControlBar, AdminSectionTabs } from "./areas/admin/AdminControlBar";
import { AdminRefreshButton } from "./areas/admin/AdminRefreshButton";
import { mountedRefreshButtons } from "./areas/admin/adminRefresh";
import { GlobalSearchField } from "./shell/GlobalSearchField";
import { ModalLayer } from "./shell/ModalLayer";
import { SettingsPanelLayer } from "./areas/settings/SettingsPanelLayer";
import { MobileNavigation } from "./shell/MobileNavigation";
import { SectionLayer } from "./shell/SectionLayer";
import { MobileAppBar } from "./shell/MobileAppBar";
import { MobileContextAction } from "./shell/MobileContextAction";
import { SidebarNav } from "./shell/SidebarNav";
import { SignInWall } from "./shell/SignInWall";
import { StatusBanner } from "./shell/StatusBanner";
import { TopbarTitle } from "./shell/TopbarTitle";
import { AccountMenu } from "./shell/AccountMenu";
import { AdminContactRequests, AdminEmailDelivery, AdminProvisioningHistory, AdminProvisioningRequests, AdminTenantLifecycle } from "./areas/admin/AdminPanelLists";
import { AdminSecurityPanel } from "./areas/admin/AdminSecurityPanel";
import { AdminTrafficAnalytics } from "./areas/admin/TrafficAnalytics";
import { AdminCustomerOperations } from "./areas/admin/CustomerOperations";
import { PageHeadIsland } from "./platform/PageHead";
import { PAGE_HEADS } from "./platform/pageCopy";
import { findIslandHost } from "./legacy/bridge";

/** Entry point for the React shell bundle.
 *
 *  React does not own the document. index.html is 5,000 lines of hand-written
 *  markup that `app.js` queries and binds against at load, so React mounts into
 *  specific hosts inside that page and grows outward from them — one area at a
 *  time — rather than replacing the page in a single step.
 *
 *  Each entry below is one such island. The list is the migration's progress
 *  bar: it grows as areas port, and the last thing to move is the shell itself,
 *  because the shell is what the legacy bootstrap is most entangled with.
 */
const ISLANDS = [
  /* The shell's first island. Every area in this list reaches back through the
     bridge for `setStatus`, and this is what draws what they say. */
  { host: "statusBanner", render: () => <StatusBanner /> },
  /* The React-owned presentation state for the legacy section shells. */
  { host: "sectionLayerHost", render: () => <SectionLayer /> },
  /* The topbar, in two islands with the search field between them: the page it
     names, and the account chip with its menu. The search field and its
     drop-down are still app.js's -- a type-ahead with a debounce, an abort
     controller and a roving `aria-activedescendant`. */
  /* The topbar's global search: the field, its live region and its drop-down.
     A combobox whose options were an innerHTML template string, and whose
     roving aria-activedescendant is the only thing tying the two together --
     focus stays in the input so typing can continue. */
  { host: "globalSearchFieldHost", render: () => <GlobalSearchField /> },
  { host: "topbarTitleGroup", render: () => <TopbarTitle /> },
  { host: "topbarAccountMenu", render: () => <AccountMenu /> },
  /* The phone's top bar and floating action over its card lists. The drawer,
     backdrop and toggle are composed by MobileNavigation below; app.js keeps
     only the focus, inert-shell and responsive-navigation service. */
  { host: "mobileAppBar", render: () => <MobileAppBar /> },
  { host: "mobileContextAction", render: () => <MobileContextAction /> },
  /* Every modal in the app, and which of them are open. Twenty-one containers
     stood in index.html, each an island host that app.js reached into to add
     and remove `hidden` — so which modal was open lived in the DOM. The stack
     is a list of ids now, announced, and the layer draws the containers from
     it. What did not move is the stack: app.js is the caller of every open and
     holds the element focus goes back to. */
  { host: "modalLayer", render: () => <ModalLayer /> },
  /* Thirteen destinations from one table. app.js still routes; this decides how
     each row looks and asks it to. */
  { host: "sidebarNav", render: () => <SidebarNav /> },
  /* The same thirteen destinations on a phone, from the same table. */
  /* The phone's navigation: the drawer, its backdrop and the toggle above
     them. Two of those were app.js's markup, kept in step with six setAttribute
     calls off one boolean. */
  { host: "mobileNavigationHost", render: () => <MobileNavigation /> },
  /* The sign-in wall's form: six modes, one at a time. The section around it
     is app.js's — it shows and hides the wall with the session — and so is
     every request the form makes. The last modal of the shell, and the one the
     modal stack itself was waiting on. */
  { host: "authFormCard", render: () => <SignInWall /> },
  { host: "reportsArea", render: () => <ReportsArea /> },
  /* The recorded range sessions. Its own container beside the detail view
     rather than sharing one: app.js wrote the list, a session's detail and the
     correction form into a single element in turn, and an island cannot
     survive that. */
  { host: "rangeReportsList", render: () => <RangeSessions /> },
  /* The whole workspace search results page. It was two islands — the stat
     tiles and the cards — separated in the markup by filter controls app.js
     owned; the controls, the head and the pager are React's now, so the two are
     composed into one. */
  { host: "searchResultsPage", render: () => <SearchResultsPage /> },
  /* One session's detail, in the container beside the list. Four views of one
     record — the report, a form to correct it, and per-result cards that each
     become a form in place — which app.js drew by assigning innerHTML over
     whichever was showing. */
  { host: "rangeReportsDetail", render: () => <RangeReportDetail /> },
  /* The Performance Trends filter card. Two of its selects were the first
     React regions to read the shared inventory store; the card around them is
     the last part of this page to port. The six values live in app.js, which
     still owns the request they build. */
  { host: "insightsFilters", render: () => <InsightsFilters /> },
  /* The trend summaries themselves: four cards, two charts, the session table
     and the best recorded results. The host is the card, and the panel draws
     four states into it — including a 403, which is not a failure and so gets
     no retry. */
  { host: "insightsContent", render: () => <TrendSummaries /> },
  /* Cost of Precision, under the trend summaries on Performance Trends. It
     fetches /insights/cost rather than reading the store, so it draws its own
     loading and its own failure -- and the failure now offers a retry, which
     the legacy panel did not: it wrote the server's message into the element
     and left the reader a reload as the only way back. */
  { host: "insightsCostContent", render: () => <CostOfPrecision /> },
  {
    host: "reloadingToolbar",
    render: () => (
      <InventoryToolbar
        resource="reloading"
        searchInputId="reloadingSupplySearch"
        pageSizeId="reloadingSupplyPageSize"
        archiveToggle
      />
    ),
  },
  { host: "reloadingTable", render: () => <ReloadingTable /> },
  /* Paging is its own island rather than part of the table: in the markup it is
     a sibling of the table, outside it, and the page-size select that belongs
     with it is further up in the toolbar with the filters it ports alongside. */
  {
    host: "reloadingPagination",
    render: () => <InventoryPagination resource="reloading" noun="reloading supplies" />,
  },
  { host: "suppressorPagination", render: () => <InventoryPagination resource="suppressors" noun="suppressors" /> },
  /* Ammunition's rows-per-page select is in this row rather than in the toolbar,
     so the pagination island renders it. The others put it in the toolbar. */
  {
    host: "ammoPagination",
    render: () => <InventoryPagination resource="ammo" noun="ammunition" pageSizeId="ammoPageSize" />,
  },
  { host: "firearmPagination", render: () => <InventoryPagination resource="firearms" noun="firearms" /> },
  /* Search, rows-per-page and the filter menu. One component for all four
     lists; which fields each offers is data in platform/inventoryFilters.ts. */
  {
    host: "suppressorToolbar",
    render: () => (
      <InventoryToolbar resource="suppressors" searchInputId="suppressorSearch" pageSizeId="suppressorPageSize" />
    ),
  },
  { host: "suppressorsTable", render: () => <SuppressorsTable /> },
  /* The tiles and the Top Calibers chart above the ammunition list. The host is
     the summary shell itself, so the two halves stay its direct children and
     keep the grid the stylesheet gives them. These describe the workspace; the
     strip further down describes the loaded page. */
  { host: "ammoSummaryShell", render: () => <AmmoOverview /> },
  /* The Totals subpage beside Inventory. The host is that tab panel, which
     app.js shows and hides -- the panel used to be cloned in from a
     `<template>` the first time the tab was opened, and an island cannot mount
     into markup that is not there yet. */
  { host: "ammo-subpage-panel-totals", render: () => <AmmoCaliberTotals /> },
  /* The only inventory toolbar with an Add button, so it passes one through.
     Both halves of that button are still legacy: a workspace can rename it in
     Settings, and it opens the entry workspace, which has not ported. */
  {
    host: "ammoToolbar",
    render: () => (
      <InventoryToolbar resource="ammo" searchInputId="ammoSearch">
        <AmmoAddButton />
      </InventoryToolbar>
    ),
  },
  // Ammunition alone has a caption and insight strip between toolbar and table.
  { host: "ammoInventoryStrip", render: () => <AmmoInventorySummary /> },
  { host: "ammoTable", render: () => <AmmoTable /> },
  /* The entry workspace, and with it all six entry forms.
     Each of the six used to be an island of its own, mounted into a card that
     app.js moved between a host, a hidden parking `<div>` and — for four of them
     — an edit modal. The workspace holds which form is open and draws every one
     of them; the inactive ones are hidden rather than unmounted, so a
     half-filled record survives a look at another form.

     The four islands below it are the same forms again, drawn inside the modal a
     row's Edit button opens. A form renders in one place or the other, never
     both: while an area's draft holds a record, its workspace slot renders
     nothing and its modal renders the form. That is what replaced carrying one
     card between two parents. */
  { host: "entryWorkspaceHost", render: () => <EntryWorkspace /> },
  /* Two of Settings' seven tab panels. The tab strip is still app.js's, so each
     host is the panel it shows and hides; what is inside is React's.

     They are the two that are only about *this account*: how the workspace
     looks to it, and whether it is emailed the low-stock digest. Both read one
     subscription -- what app.js knows about the account -- because every answer
     in them arrives with the session, which resolves long after these mount. */
  /* The Settings tab strip. */
  { host: "settingsTabsHost", render: () => <SettingsTabs /> },
  /* And six of its seven panels, from one table: Profile, Workspace,
     Preferences, Notifications, Backup and the whole of Security — the
     summary, the three enrolment cards in their stack, and the password
     change. Each was its own island in a panel app.js showed and hid by
     toggling five attributes over a NodeList captured at boot; which tab is
     showing is the announced answer the strip above already reads.

     The seventh, Workspace Setup, is still index.html's: app.js relocates the
     admin page's twelve configuration panels into it, and React cannot own a
     container whose children another file appends. */
  { host: "settingsPanelLayer", render: () => <SettingsPanelLayer /> },
  {
    host: "firearmToolbar",
    render: () => (
      <InventoryToolbar resource="firearms" searchInputId="firearmSearch" pageSizeId="firearmPageSize" />
    ),
  },
  { host: "firearmsTable", render: () => <FirearmsTable /> },
  /* The Manufacturers subpage beside Inventory. The host is that tab panel,
     which is itself the card -- so the island renders the card's head and table
     rather than a card of its own. It reads the complete inventory: app.js
     summarised the loaded page and called the result the workspace's totals. */
  { host: "firearm-subpage-panel-manufacturers", render: () => <FirearmManufacturers /> },
  /* On the dashboard rather than in the Firearms area, and the first panel
     whose filters are React state outright -- neither triggers a request. */
  { host: "maintenanceQueue", render: () => <MaintenanceQueue /> },
  /* The two operations lists beside it. Each mounts into its own <ol>, so
     React renders the rows and the list element stays where it was. */
  /* The Action Board above them, which is the only one of the four that
     makes a claim rather than listing what it was handed. */
  { host: "homeNextActionsList", render: () => <ActionBoard /> },
  /* The four inventory totals at the top of the dashboard, and the notice that
     appears under them when a collection could not be loaded. The cards are
     buttons, so this island owns the navigation as well as the figures. */
  { host: "homeStatCards", render: () => <HomeStatCards /> },
  { host: "homeResourceStatusHost", render: () => <HomeResourceStatus /> },
  { host: "homeLowStockList", render: () => <LowStockWatchList /> },
  { host: "homeRecentActivityList", render: () => <RecentActivity /> },
  // Not an inventory resource: a per-firearm sub-list, on its own subscription.
  { host: "ballisticsTable", render: () => <BallisticsTable /> },
  /* The five cards above that table and the correlation snapshot below it, on
     the same subscription. The snapshot's host is the <ul> itself, so React
     renders the items and the list element stays where it was. */
  { host: "ballisticsSummaryCards", render: () => <BallisticsSummaryCards /> },
  { host: "ballisticsCorrelationList", render: () => <CorrelationSnapshot /> },
  /* Profile Performance, on the same subscription again. The host is the whole
     section: the phone cards and the table are two views of one list, and the
     stylesheet shows exactly one of them at a time. */
  { host: "ballisticsProfilePerformance", render: () => <ProfilePerformance /> },
  { host: "ballisticsLoadDevelopment", render: () => <LoadDevelopment /> },
  /* The first island that renders a request rather than a store read: this
     panel fetches its own endpoint, so its loading and its failure are drawn
     here from the state app.js reports. */
  { host: "ballisticsComponentStock", render: () => <ComponentStock /> },
  /* Two more bench panels on the same pattern: each fetches its own endpoint,
     so each draws its own loading, its own failure and its own retry. */
  { host: "loadsList", render: () => <Recipes /> },
  { host: "brassLotsList", render: () => <BrassLots /> },
  { host: "workupsList", render: () => <Workups /> },
  { host: "ballisticsBarrelWear", render: () => <BarrelWear /> },
  /* The admin customization form: twelve cards from one table, each mounted
     into its own <section> rather than the panel around it. The panels still
     hold cards app.js owns -- the brand image uploads, the five table-layout
     editors, the caliber low-stock defaults, and the two ammunition toggles --
     and an island that took a whole panel would take those with it. */
  /* The Section Manager, on the same draft: rows rather than a field grid, so
     its own component. The brand images are a third shape again -- a preview, a
     field and two buttons -- and one of those buttons opens a file dialog. */
  /* The five table-layout editors, which this plan deferred through two
     stages: which columns an inventory table shows, in what order, and what
     this workspace calls each one. The rows *were* the answer until now --
     app.js read them back out of the page when Save was pressed -- so it is
     told on every change instead. */

  /* And the caliber thresholds beside them, deferred with them. */
  /* Not part of the customization document at all: the four organization roles
     and what each grants, which app.js announces as constants. */
  /* The Platform members subpage: which organization is being administered,
     who is in it, what each role can do, and the add-member card between them.
     Six hosts and a Refresh button inside four <section>s, the last of which
     app.js hid by permission. */
  { host: "admin-subpage-panel-members", render: () => <AdminMembersPanel /> },
  /* The Platform users subpage: the directory, the add-user form its button
     reveals, and the card for whoever is selected. Six hosts inside three
     <section>s, two of which app.js was still reaching into -- one by class,
     one by a data attribute the stylesheet keys on. */
  { host: "admin-subpage-panel-users", render: () => <AdminUsersPanel /> },
  /* Platform Administration's navigation: the picker with its label and hint,
     and the tablist beside it. One table, drawn from the router's announcement;
     the three destinations used to be written out here, in index.html and in
     features/admin/registry.js. */
  { host: "adminControlBar", render: () => <AdminControlBar /> },
  /* The five panel Refresh buttons, one per platform panel. Each was its own
     element, listener and loader call; what crosses now is the panel's name. */
  ...mountedRefreshButtons().map((button) => ({
    host: button.host,
    render: () => <AdminRefreshButton button={button} />,
  })),
  { host: "adminSectionTabs", render: () => <AdminSectionTabs /> },
  /* The platform security panel: five cards, of which two are drawn only for a
     reader who may use them. Three were island hosts here and the two override
     editors were a spread; the panel composes all five, because a wrapper app.js
     hides by permission cannot port while what is inside it is a host. */
  { host: "admin-subpage-panel-security", render: () => <AdminSecurityPanel /> },
  /* The organization directory, on the Org management section. */
  /* The Org management subpage: three cards, offered together or not at all.
     Each was a host inside a <section> app.js hid by permission, and a wrapper
     cannot port while what is inside it is a host -- so the panel composes
     them. */
  { host: "admin-subpage-panel-organizations", render: () => <AdminOrganizationsPanel /> },
  /* Aggregate-only traffic lives in its own Platform Administration section;
     it is deliberately not mounted into the customer-request panels. */
  { host: "admin-subpage-panel-traffic-analytics", render: () => <AdminTrafficAnalytics /> },
  { host: "admin-subpage-panel-customer-operations", render: () => <AdminCustomerOperations /> },
  /* The account card's organization checklist. Its twin is rendered inside the
     add-user form rather than mounted, because that form is already React. */
  /* The five admin event lists. One shape, one item component, five shapings --
     and the buttons inside them are still driven by app.js's delegated
     listeners on these very containers. */
  { host: "adminContactRequestsList", render: () => <AdminContactRequests /> },
  { host: "adminEmailDelivery", render: () => <AdminEmailDelivery /> },
  { host: "adminProvisioningRequestsList", render: () => <AdminProvisioningRequests /> },
  { host: "adminProvisioningHistory", render: () => <AdminProvisioningHistory /> },
  { host: "adminTenantLifecycleList", render: () => <AdminTenantLifecycle /> },
  /* The logging panel. The audit history is the server's; the six cards below
     it never left this browser, which is the point of them. */
  /* The operations log: four filters, three summary tiles, the list and its
     paging. The last stateful thing on the admin page. */
  /* The four cards that are not the customization document: two switches with
     endpoints of their own, the backups row -- which is where the workspace CSV
     export lives, so that button is drawn here rather than in a host of its
     own -- and the Save/Reset pair that writes the document. */

  /* The five card heads a workspace can rename. One component and one table:
     the wording used to live in index.html *and* be assigned by app.js on every
     customization apply, and three of the five markup sentences had drifted
     from the default they stood in for. */
  ...PAGE_HEADS.map((head) => ({
    host: head.host,
    render: () => <PageHeadIsland head={head} />,
  })),
  /* The Bench's Add button, whose label is the same customization its head
     reads. The ammunition one is passed through the toolbar island above; this
     toolbar is not React, so it is its own. */
  { host: "ballisticsAddButtonHost", render: () => <BallisticsAddButton /> },
  /* The Bench's toolbar: the firearm filter, the sentence under it and the
     banner under that, all three from the one announcement the entry form's own
     selector already reads. Which firearm is selected used to be the select's
     own value. */
  { host: "ballisticsFirearmFilter", render: () => <BallisticsToolbar /> },
  { host: "ballisticsPageFirearmHelp", render: () => <BallisticsFirearmHelp /> },
  { host: "ballisticsPageStatus", render: () => <BallisticsPageStatus /> },
  /* The three bench lists' heads, each a heading and an Add button. */
  ...BENCH_ADD_BUTTONS.map((entry) => ({
    host: entry.host,
    render: () => <BenchListHead entry={entry} />,
  })),
];

function mountIslands() {
  for (const island of ISLANDS) {
    const host = findIslandHost(island.host);
    // Absent is normal, not an error: an island's host only exists once its
    // markup has been handed over, and this bundle also loads on pages that
    // have not been touched yet.
    if (!host) continue;
    createRoot(host).render(<StrictMode>{island.render()}</StrictMode>);
  }
}

/* This bundle is a module script, so it is deferred and the document is parsed
   by the time it runs. app.js is a classic script placed before it, so its
   bootstrap has also finished — which is what lets an island call back through
   the legacy bridge on its first render. */
mountIslands();
